import { Location } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import {
    ActivatedRouteSnapshot,
    NavigationCancel,
    Router,
    RouterStateSnapshot,
    UrlTree,
} from '@angular/router';
import {
    filter, map,
    Observable,
} from 'rxjs';
import {take, tap} from 'rxjs/operators';

import { AuthorisationFacade } from '~/app/core/state/authorisation/authorisation.facade';
import { BasePermissionsService } from '~/app/shared/services/base-permissions/base-permissions.service';
import { BinaryOperator } from '~/app/shared/types/binary-operator.type';

@Injectable({
    providedIn: 'root',
})
export class AuthorisationGuard {
    private authorisationService = inject(BasePermissionsService);
    private authorisationFacade = inject(AuthorisationFacade);
    private router = inject(Router);
    private location = inject(Location);

    private previousUrl: string | null = null;

    constructor() {
        this.router.events
            .subscribe((event) => {
                if (event instanceof NavigationCancel) {
                    this.previousUrl = event.url;
                }
            });
    }

    canActivate(
        route: ActivatedRouteSnapshot,
        state: RouterStateSnapshot,
    ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
        const routeDataPermissions = route.data.permissions as string[];
        const binaryOperator = (route.data.binaryOperator ?? 'AND') as BinaryOperator;

        return this.authorisationFacade.permissionsLoaded$.pipe(
            tap((loaded) => {
                if (!loaded) this.authorisationFacade.getAuthorisations();
            }),
            filter((loaded) => loaded),
            take(1),

            map(() => {
                if (!routeDataPermissions?.length) {
                    return true;
                }

                const isAllowed = this.authorisationService.hasPermissions(
                    routeDataPermissions,
                    null,
                    binaryOperator,
                );

                if (isAllowed) {
                    return true;
                }
                // Pas autorisé -> redirection login avec redirect=state.url
                void this.router.navigate(['/auth/login'], {
                    queryParams: { redirect: state.url },
                    state: { bypassGuardAndResolver: true },
                });

                return false;
            }),
        );
    }
}
